import React, { forwardRef, useId } from 'react';
import Button, { type ButtonSize } from './Button';
import { FOCUS_RING } from './focusRing';

export type ToggleSize = 'small' | 'medium';

/**
 * Which family of toolbar controls the chrome should match. `pill` matches the
 * filter-bar inputs (`Search`, `MultiSelect`); `button` matches the buttons.
 */
export type ToggleVariant = 'pill' | 'button';

/** What the checked state means. 'pending' paints the track amber, for a switch whose change
 *  has not been written yet. The unchecked track is unaffected. */
export type ToggleTone = 'default' | 'pending';

interface ToggleProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    size?: ToggleSize;
    tone?: ToggleTone;
    /** Visible text shown before the switch (e.g. a filter label). */
    label?: string;
    /**
     * Chrome for a toolbar filter — pick the family it sits beside. `pill` is the
     * filter-bar look that matches `Search`/`MultiSelect`; `button` renders the whole
     * control as an outline `Button`. Setting this implies `bordered`.
     */
    variant?: ToggleVariant;
    /** Shorthand for `variant="pill"`. */
    bordered?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    'aria-describedby'?: string;
    className?: string;
    id?: string;
}

const KNOB_INSET = 2;

const SIZE_DIMENSIONS: Record<ToggleSize, { track: number; height: number; knob: number }> = {
    small: { track: 28, height: 16, knob: 12 },
    medium: { track: 36, height: 20, knob: 16 },
};

// The `button` variant delegates its chrome to `Button` rather than carrying a
// parallel copy of the border/radius/padding/typography classes. The `pill` chrome
// below is what happens without that discipline: it was the only bordered look, and
// it drifted from the buttons it sat next to (card tokens, `h-8`, `rounded-xl`
// against `btn-outline`, 34px, `rounded-lg`), so those toolbars read as two
// unrelated control families (ENG-1453).
const BUTTON_VARIANT_SIZE: Record<ToggleSize, ButtonSize> = {
    small: 'small',
    medium: 'medium',
};

const CONTAINER_BASE =
    'inline-flex items-center gap-1.5 shrink-0 text-[length:var(--malachite-font-size-small,13px)] font-medium select-none';
// Colours come from card tokens so it themes in dark mode; `.shadow-default` is a
// plain box-shadow rule (shadow-DOM-safe, unlike Tailwind `shadow-*`/`ring-*` which
// rely on @property).
const PILL_CHROME =
    'px-2.5 rounded-xl whitespace-nowrap bg-[var(--malachite-card-background)] border border-[var(--malachite-card-border)] shadow-default text-[var(--malachite-card-foreground)]';
const PILL_CHROME_HEIGHT: Record<ToggleSize, string> = {
    small: 'h-[var(--malachite-control-height-small,28px)]',
    medium: 'h-[var(--malachite-control-height-medium,32px)]',
};

export const Toggle = forwardRef<HTMLElement, ToggleProps>(
    (
        {
            checked,
            onChange,
            disabled = false,
            size = 'medium',
            tone = 'default',
            label,
            variant,
            bordered = false,
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledBy,
            'aria-describedby': ariaDescribedBy,
            className = '',
            id,
        },
        ref,
    ) => {
        const { track, height, knob } = SIZE_DIMENSIONS[size];
        const knobOffset = checked ? track - knob - KNOB_INSET : KNOB_INSET;

        // `variant` implies chrome, so `variant="button"` works on its own rather than
        // silently doing nothing without `bordered`.
        const chrome = variant ?? (bordered ? 'pill' : undefined);

        // When a visible label is rendered and the consumer hasn't supplied their own
        // labelledby, the visible text becomes the switch's accessible name. An
        // empty-string label or labelledby counts as absent, so it can't render an
        // empty <span> (with stray gap) or wire up a broken/empty aria linkage.
        const generatedLabelId = useId();
        const hasLabel = Boolean(label);
        const visibleLabelId = hasLabel && !ariaLabelledBy ? generatedLabelId : undefined;
        const resolvedLabelledBy = ariaLabelledBy || visibleLabelId;

        const trackStyle: React.CSSProperties = {
            position: 'relative',
            display: 'inline-block',
            flexShrink: 0,
            width: `${track}px`,
            height: `${height}px`,
            borderRadius: '999px',
            transition: 'background-color 150ms ease-in-out',
            backgroundColor: checked
                ? tone === 'pending'
                    ? 'var(--malachite-toggle-track-checked-pending, rgb(241, 139, 10))'
                    : 'var(--malachite-toggle-track-checked, rgb(0, 175, 102))'
                : 'var(--malachite-toggle-track, rgb(232, 232, 232))',
        };

        const knobSpan = (
            <span
                style={{
                    position: 'absolute',
                    top: `${KNOB_INSET}px`,
                    left: `${knobOffset}px`,
                    width: `${knob}px`,
                    height: `${knob}px`,
                    borderRadius: '50%',
                    backgroundColor: 'var(--malachite-toggle-knob, rgb(255, 255, 255))',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                    transition: 'left 150ms ease-in-out',
                }}
            />
        );

        // The whole button-variant control is the switch — a button-shaped surface whose
        // label isn't clickable reads as broken. That also rules out nesting the switch
        // `<button>` inside `Button`'s, so here the track is a decorative span and
        // `Button` carries the switch role. Disabled dimming comes from
        // `.btn-outline:disabled`, so the track must not dim itself as well.
        if (chrome === 'button') {
            return (
                <Button
                    ref={ref as React.Ref<HTMLButtonElement>}
                    variant="outline"
                    size={BUTTON_VARIANT_SIZE[size]}
                    type="button"
                    role="switch"
                    id={id}
                    aria-checked={checked}
                    aria-label={ariaLabel}
                    aria-labelledby={resolvedLabelledBy}
                    aria-describedby={ariaDescribedBy}
                    disabled={disabled}
                    onClick={() => onChange(!checked)}
                    icon={<span style={trackStyle}>{knobSpan}</span>}
                    iconPosition="end"
                    className={`whitespace-nowrap select-none ${className}`.trim()}
                >
                    {hasLabel && <span id={visibleLabelId}>{label}</span>}
                </Button>
            );
        }

        const isSwitchRoot = !hasLabel && !chrome;
        const switchButton = (
            <button
                ref={isSwitchRoot ? (ref as React.Ref<HTMLButtonElement>) : null}
                type="button"
                role="switch"
                id={id}
                aria-checked={checked}
                aria-label={ariaLabel}
                aria-labelledby={resolvedLabelledBy}
                aria-describedby={ariaDescribedBy}
                disabled={disabled}
                onClick={() => onChange(!checked)}
                className={`${FOCUS_RING} ${hasLabel || chrome ? '' : className}`.trim()}
                style={{
                    ...trackStyle,
                    padding: 0,
                    border: 'none',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                {knobSpan}
            </button>
        );

        if (isSwitchRoot) {
            return switchButton;
        }

        const containerClasses =
            `${CONTAINER_BASE}${chrome === 'pill' ? ` ${PILL_CHROME_HEIGHT[size]} ${PILL_CHROME}` : ''} ${className}`.trim();

        return (
            <span ref={ref as React.Ref<HTMLSpanElement>} className={containerClasses}>
                {hasLabel && <span id={visibleLabelId}>{label}</span>}
                {switchButton}
            </span>
        );
    },
);

Toggle.displayName = 'Toggle';

export default Toggle;
